// geographycontent11.js
// Section Eleven — Geographical Enquiry and Skills
// Grade9Ready Geography content module
//
// Covers:
// 145 What is Geographical Enquiry?
// 146 Sources of Information
// 147 Geographical Enquiry
// 150 Ordnance Survey Maps
// 153 Human Geography — Plans and Photos
// 156 Describing Maps and Charts
// 158 Types of Graphs and Charts
// 167 Revision Summary for Section Eleven

export const geographyContent11 = {
  id: "geography-section-11",
  subject: "Geography",
  sectionNumber: 11,
  title: "Geographical Enquiry and Skills",
  slug: "geographical-enquiry-and-skills",
  examBoardLink:
    "KS3 Geography: geographical enquiry, fieldwork, OS maps, grid references, scale, photographs, GIS, graphs, charts and data interpretation",
  intro:
    "This section develops the practical skills geographers use to investigate places, patterns and processes. It covers geographical enquiry, sources of information, fieldwork, Ordnance Survey maps, plans, photographs, map and chart description, and different types of graphs. The focus is on asking good geographical questions, collecting reliable evidence, presenting data clearly and reaching supported conclusions.",
  keySkills: [
    "Ask clear geographical enquiry questions.",
    "Distinguish between primary and secondary sources of information.",
    "Collect, record and evaluate geographical data.",
    "Use Ordnance Survey map symbols, scale and grid references.",
    "Interpret plans, photographs, aerial images and satellite images.",
    "Describe map and chart patterns accurately.",
    "Choose appropriate graphs and charts for different types of data.",
    "Use evidence to reach justified geographical conclusions."
  ],

  topics: [
    {
      id: "geo11-1-what-is-geographical-enquiry",
      title: "What is Geographical Enquiry?",
      slug: "what-is-geographical-enquiry",
      pageRef: "Source booklet p.145",
      learningObjectives: [
        "Define geographical enquiry.",
        "Explain why geographers ask questions.",
        "Understand the stages of an enquiry."
      ],
      keywords: [
        {
          term: "Geographical enquiry",
          definition:
            "A structured investigation into a geographical question, issue, place, pattern or process."
        },
        {
          term: "Enquiry question",
          definition:
            "A focused question that guides a geographical investigation."
        },
        {
          term: "Hypothesis",
          definition:
            "A testable statement or prediction that can be investigated using evidence."
        },
        {
          term: "Evidence",
          definition:
            "Information or data used to support an answer, explanation or conclusion."
        },
        {
          term: "Data",
          definition:
            "Information collected during an investigation, often in the form of numbers, observations or descriptions."
        },
        {
          term: "Conclusion",
          definition:
            "A final judgement or answer based on evidence."
        },
        {
          term: "Evaluation",
          definition:
            "A judgement about the strengths, weaknesses, reliability and limitations of an enquiry."
        }
      ],
      tutorial: [
        {
          heading: "What enquiry means in geography",
          body:
            "Geographical enquiry is the process of investigating a question about the world. It might ask why a place is changing, how land use varies, whether a river changes downstream, or how people affect an environment."
        },
        {
          heading: "The enquiry sequence",
          body:
            "A strong enquiry usually follows a sequence: ask a question, plan the investigation, collect data, present the data, analyse patterns, reach a conclusion and evaluate the method."
        },
        {
          heading: "Why enquiry matters",
          body:
            "Enquiry helps geographers move beyond opinion. Instead of guessing, they gather evidence and use it to explain patterns, test ideas and make decisions."
        },
        {
          heading: "Good enquiry questions",
          body:
            "Good enquiry questions are focused, geographical and possible to investigate. For example, ‘How does traffic vary along the high street?’ is stronger than ‘What is traffic like?’ because it gives a clear focus."
        }
      ],
      workedExamples: [
        {
          question:
            "Turn this broad topic into a geographical enquiry question: traffic in town centres.",
          answer:
            "A focused enquiry question could be: ‘How does traffic volume change between the edge and centre of the town?’ This is geographical, measurable and linked to location."
        },
        {
          question:
            "Why is evaluation important in geographical enquiry?",
          answer:
            "Evaluation is important because it identifies how reliable the evidence is. It considers whether the method was fair, whether enough data was collected, and how the enquiry could be improved."
        }
      ],
      commonMisconceptions: [
        "An enquiry is not just collecting facts; it is a structured investigation.",
        "A hypothesis is not a proven fact. It is a statement to test.",
        "A conclusion should be based on evidence, not personal opinion."
      ],
      quickCheck: [
        {
          question: "What is a geographical enquiry?",
          options: [
            "A structured investigation into a geographical question",
            "A list of random facts",
            "A drawing with no data",
            "A weather forecast only"
          ],
          answer:
            "A structured investigation into a geographical question",
          explanation:
            "Geographical enquiry uses evidence to investigate places, patterns and processes."
        },
        {
          question: "Which is the best enquiry question?",
          options: [
            "How does pedestrian flow vary along the high street?",
            "Do I like the high street?",
            "Is geography fun?",
            "What is a shop?"
          ],
          answer:
            "How does pedestrian flow vary along the high street?",
          explanation:
            "It is focused, geographical and can be investigated with data."
        }
      ],
      examPractice: [
        {
          question:
            "Describe the main stages of a geographical enquiry.",
          marks: 6,
          indicativeContent:
            "A geographical enquiry begins with an enquiry question or hypothesis. The geographer then plans how to collect data, including where, when and how measurements will be taken. Data is collected and recorded, then presented using maps, graphs or tables. The results are analysed to identify patterns and explanations. A conclusion is reached using evidence, and the method is evaluated for reliability and improvements."
        }
      ],
      grade9Move:
        "Use the full enquiry chain: question → method → data → presentation → analysis → conclusion → evaluation. This gives your answer a clear geographical structure."
    },

    {
      id: "geo11-2-sources-of-information",
      title: "Sources of Information",
      slug: "sources-of-information",
      pageRef: "Source booklet p.146",
      learningObjectives: [
        "Identify different sources of geographical information.",
        "Distinguish between primary and secondary data.",
        "Evaluate the reliability of sources."
      ],
      keywords: [
        {
          term: "Primary data",
          definition:
            "Information collected first-hand by the investigator, such as fieldwork measurements, questionnaires or observations."
        },
        {
          term: "Secondary data",
          definition:
            "Information collected by someone else, such as census data, maps, websites, books or satellite images."
        },
        {
          term: "Quantitative data",
          definition:
            "Numerical data that can be counted, measured or statistically analysed."
        },
        {
          term: "Qualitative data",
          definition:
            "Descriptive data based on opinions, observations, feelings or written descriptions."
        },
        {
          term: "Reliability",
          definition:
            "How trustworthy and consistent information is."
        },
        {
          term: "Bias",
          definition:
            "A one-sided or unfair presentation of information."
        },
        {
          term: "Source",
          definition:
            "Where information comes from."
        }
      ],
      tutorial: [
        {
          heading: "Primary sources",
          body:
            "Primary data is collected directly by the person carrying out the enquiry. In geography this could include traffic counts, pedestrian counts, river measurements, environmental quality surveys, questionnaires, sketches or photographs taken during fieldwork."
        },
        {
          heading: "Secondary sources",
          body:
            "Secondary data has already been collected by someone else. Examples include Ordnance Survey maps, census data, climate graphs, government statistics, newspaper reports, books, websites, aerial photographs and GIS layers."
        },
        {
          heading: "Quantitative and qualitative data",
          body:
            "Quantitative data uses numbers, such as traffic count totals, river depth, temperature or population density. Qualitative data is descriptive, such as field sketches, interview comments or environmental quality descriptions."
        },
        {
          heading: "Checking reliability",
          body:
            "A source is more reliable if it is accurate, up to date, relevant, unbiased and collected using a clear method. Geographers should compare sources rather than rely on one piece of information."
        }
      ],
      workedExamples: [
        {
          question:
            "Classify these sources: a traffic count, an OS map, a questionnaire, census data.",
          answer:
            "Traffic count = primary and quantitative. OS map = secondary. Questionnaire = primary, and may be quantitative or qualitative depending on the questions. Census data = secondary and quantitative."
        },
        {
          question:
            "Why might a website be less reliable than official census data?",
          answer:
            "A website may be out of date, biased or unclear about where its information came from. Official census data usually has a clearer method and is collected systematically."
        }
      ],
      commonMisconceptions: [
        "Primary data is not automatically better than secondary data. It depends on accuracy and method.",
        "Secondary data is not automatically unreliable. Official data can be very useful.",
        "Questionnaires can produce both numbers and opinions, so they can be quantitative and qualitative."
      ],
      quickCheck: [
        {
          question:
            "Which is an example of primary data?",
          options: [
            "A pedestrian count you carry out",
            "A census table from a government website",
            "An old textbook map",
            "A satellite image downloaded from a database"
          ],
          answer: "A pedestrian count you carry out",
          explanation:
            "Primary data is collected first-hand by the investigator."
        },
        {
          question:
            "Which type of data is numerical?",
          options: [
            "Quantitative data",
            "Qualitative data",
            "Biased data only",
            "Descriptive data only"
          ],
          answer: "Quantitative data",
          explanation:
            "Quantitative data is based on numbers and measurements."
        }
      ],
      examPractice: [
        {
          question:
            "Explain the difference between primary and secondary data.",
          marks: 4,
          indicativeContent:
            "Primary data is collected first-hand by the investigator, such as traffic counts, questionnaires or river measurements. Secondary data has already been collected by someone else, such as census data, OS maps, websites or climate statistics. Both can be useful, but their reliability depends on how accurate, relevant and up to date they are."
        },
        {
          question:
            "Explain how a geographer can judge whether a source of information is reliable.",
          marks: 6,
          indicativeContent:
            "A geographer can check whether the source is up to date, relevant to the enquiry and produced by a trustworthy organisation. They can check whether the method is clear and whether the data could be biased. They can also compare the source with other sources to see whether the information is consistent."
        }
      ],
      grade9Move:
        "When evaluating sources, use the criteria: accuracy, date, bias, relevance, method and scale."
    },

    {
      id: "geo11-3-geographical-enquiry",
      title: "Geographical Enquiry",
      slug: "geographical-enquiry",
      pageRef: "Source booklet p.147",
      learningObjectives: [
        "Plan a geographical enquiry.",
        "Choose appropriate data collection methods.",
        "Explain how to improve enquiry reliability."
      ],
      keywords: [
        {
          term: "Fieldwork",
          definition:
            "Practical geographical investigation carried out outside the classroom."
        },
        {
          term: "Sampling",
          definition:
            "Selecting a smaller number of people, places or measurements to represent a larger group or area."
        },
        {
          term: "Random sampling",
          definition:
            "Choosing sample points or people by chance."
        },
        {
          term: "Systematic sampling",
          definition:
            "Taking samples at regular intervals, such as every 10 metres or every fifth person."
        },
        {
          term: "Stratified sampling",
          definition:
            "Taking samples from different groups or categories in proportion to their importance or size."
        },
        {
          term: "Pilot study",
          definition:
            "A small test of a method before the main enquiry."
        },
        {
          term: "Risk assessment",
          definition:
            "Identifying possible hazards and planning how to reduce risk during fieldwork."
        },
        {
          term: "Anomaly",
          definition:
            "A result that does not fit the general pattern."
        }
      ],
      tutorial: [
        {
          heading: "Planning an enquiry",
          body:
            "Planning involves deciding the enquiry question, location, data needed, methods, equipment, timing, sample size and safety arrangements. A clear plan makes the data more useful and reliable."
        },
        {
          heading: "Choosing methods",
          body:
            "Different questions need different methods. Traffic questions may use traffic counts. Shopping questions may use land-use surveys or questionnaires. River questions may use width, depth, velocity and bedload measurements."
        },
        {
          heading: "Sampling",
          body:
            "Sampling is used when it is not possible to measure everything. Random sampling reduces personal choice, systematic sampling creates regular coverage, and stratified sampling makes sure different groups or areas are represented."
        },
        {
          heading: "Improving reliability",
          body:
            "Reliability can be improved by collecting enough data, repeating measurements, using clear categories, training observers, avoiding biased questions, choosing appropriate sample points and recording conditions carefully."
        }
      ],
      workedExamples: [
        {
          question:
            "Choose a method for investigating whether traffic increases towards a town centre.",
          answer:
            "Use systematic traffic counts at regular points along a transect from the edge of the town to the centre. Count vehicles for the same length of time at each site to make the results comparable."
        },
        {
          question:
            "How could a questionnaire enquiry be improved?",
          answer:
            "Use a larger sample, ask clear neutral questions, survey people at different times and locations, and avoid only asking one type of person."
        }
      ],
      commonMisconceptions: [
        "A bigger sample is usually more reliable, but only if the method is fair and well planned.",
        "A biased question can weaken an enquiry even if many people answer it.",
        "Anomalies should not simply be ignored; they should be identified and considered."
      ],
      quickCheck: [
        {
          question:
            "What is systematic sampling?",
          options: [
            "Taking samples at regular intervals",
            "Only asking your friends",
            "Choosing data that supports your opinion",
            "Ignoring all anomalies"
          ],
          answer: "Taking samples at regular intervals",
          explanation:
            "Systematic sampling uses a fixed pattern, such as every 10 metres."
        },
        {
          question:
            "Why is a risk assessment needed in fieldwork?",
          options: [
            "To identify hazards and reduce risk",
            "To make graphs colourful",
            "To replace data collection",
            "To avoid writing a conclusion"
          ],
          answer: "To identify hazards and reduce risk",
          explanation:
            "Risk assessments help keep fieldwork safe."
        }
      ],
      examPractice: [
        {
          question:
            "Explain how a geographical enquiry can be made reliable.",
          marks: 6,
          indicativeContent:
            "A geographical enquiry can be made reliable by using a clear method, collecting enough data and repeating measurements. Sampling should be appropriate, such as systematic sampling along a transect. Data should be collected at the same time interval at each site where possible. Questions should be neutral, equipment should be used accurately, and anomalies should be identified and explained."
        },
        {
          question:
            "Describe how you would investigate changes in environmental quality along a street.",
          marks: 6,
          indicativeContent:
            "I would choose several sites at regular intervals along the street using systematic sampling. At each site I would complete an environmental quality survey using the same criteria, such as litter, noise, traffic, building condition and green space. I would record scores in a table, take photographs as evidence, then present the results using a graph or map and analyse whether environmental quality changes along the street."
        }
      ],
      grade9Move:
        "When describing methods, include: where, when, how often, what equipment, what data, and how reliability will be improved."
    },

    {
      id: "geo11-4-ordnance-survey-maps",
      title: "Ordnance Survey Maps",
      slug: "ordnance-survey-maps",
      pageRef: "Source booklet p.150",
      learningObjectives: [
        "Use OS map symbols and keys.",
        "Give four-figure and six-figure grid references.",
        "Use scale, contour lines and direction on OS maps."
      ],
      keywords: [
        {
          term: "Ordnance Survey map",
          definition:
            "A detailed map of Great Britain showing physical and human features using standard symbols."
        },
        {
          term: "Grid reference",
          definition:
            "A set of numbers used to locate a position on a map."
        },
        {
          term: "Four-figure grid reference",
          definition:
            "A grid reference that identifies a whole grid square."
        },
        {
          term: "Six-figure grid reference",
          definition:
            "A more precise grid reference that identifies a location within a grid square."
        },
        {
          term: "Easting",
          definition:
            "A grid line number that increases towards the east."
        },
        {
          term: "Northing",
          definition:
            "A grid line number that increases towards the north."
        },
        {
          term: "Scale",
          definition:
            "The relationship between distance on a map and distance on the ground."
        },
        {
          term: "Contour line",
          definition:
            "A line on a map joining places of equal height above sea level."
        },
        {
          term: "Relief",
          definition:
            "The height and shape of the land."
        }
      ],
      tutorial: [
        {
          heading: "Using the key",
          body:
            "OS maps use symbols to show features such as roads, railways, rivers, woodland, buildings, campsites, churches and tourist sites. The key explains what each symbol means."
        },
        {
          heading: "Grid references",
          body:
            "Grid references use eastings first, then northings. A simple way to remember this is: along the corridor, then up the stairs. Four-figure references identify a grid square. Six-figure references give a more precise location within the square."
        },
        {
          heading: "Scale and distance",
          body:
            "Scale allows map distance to be converted into real distance. For example, on a 1:50,000 map, 1 cm on the map represents 50,000 cm, or 500 metres, on the ground."
        },
        {
          heading: "Contour lines",
          body:
            "Contour lines show height and relief. Close contour lines show steep slopes. Contour lines far apart show gentle slopes. Circular contours may show hills or depressions depending on height values."
        },
        {
          heading: "Direction",
          body:
            "Maps are usually orientated with north at the top. Compass directions and bearings can be used to describe the location of features and routes."
        }
      ],
      workedExamples: [
        {
          question:
            "What is the difference between a four-figure and six-figure grid reference?",
          answer:
            "A four-figure grid reference identifies a whole grid square. A six-figure grid reference gives a more precise position within that square."
        },
        {
          question:
            "What do close contour lines show?",
          answer:
            "Close contour lines show a steep slope because height changes quickly over a short horizontal distance."
        },
        {
          question:
            "On a 1:50,000 map, what ground distance does 2 cm represent?",
          answer:
            "1 cm represents 500 m, so 2 cm represents 1,000 m or 1 km."
        }
      ],
      commonMisconceptions: [
        "Grid references always use eastings before northings.",
        "A six-figure grid reference is more precise than a four-figure grid reference.",
        "Close contour lines mean steep land, not flat land."
      ],
      quickCheck: [
        {
          question:
            "Which comes first in a grid reference?",
          options: ["Easting", "Northing", "Height", "Scale"],
          answer: "Easting",
          explanation:
            "Grid references are read eastings first, then northings."
        },
        {
          question:
            "What do contour lines show?",
          options: [
            "Height and relief",
            "Population only",
            "Temperature only",
            "Political borders only"
          ],
          answer: "Height and relief",
          explanation:
            "Contour lines join points of equal height above sea level."
        },
        {
          question:
            "What do close contour lines indicate?",
          options: [
            "A steep slope",
            "A flat plain",
            "A larger population",
            "A wider river"
          ],
          answer: "A steep slope",
          explanation:
            "Close contours show height changes rapidly, meaning steep relief."
        }
      ],
      examPractice: [
        {
          question:
            "Explain how contour lines can be used to describe relief.",
          marks: 4,
          indicativeContent:
            "Contour lines join places of equal height above sea level. Where contour lines are close together, the land is steep because height changes quickly. Where contour lines are far apart, the land is gently sloping or flatter. The height numbers on contour lines show whether land is higher or lower."
        },
        {
          question:
            "Describe how to give a six-figure grid reference.",
          marks: 4,
          indicativeContent:
            "First read the easting to identify the grid square, then estimate tenths across the square. Next read the northing and estimate tenths up the square. Write the three easting digits followed by the three northing digits. This gives a precise six-figure grid reference."
        }
      ],
      grade9Move:
        "In map-skill answers, use precise language: eastings, northings, scale, relief, contour spacing, direction and distance."
    },

    {
      id: "geo11-5-human-geography-plans-and-photos",
      title: "Human Geography — Plans and Photos",
      slug: "human-geography-plans-and-photos",
      pageRef: "Source booklet p.153",
      learningObjectives: [
        "Interpret plans, ground photographs and aerial photographs.",
        "Identify human and physical features in images.",
        "Use photographs as geographical evidence."
      ],
      keywords: [
        {
          term: "Plan",
          definition:
            "A simplified map or drawing showing the layout of a place from above."
        },
        {
          term: "Ground photograph",
          definition:
            "A photograph taken from ground level."
        },
        {
          term: "Aerial photograph",
          definition:
            "A photograph taken from above the ground, often from an aircraft or drone."
        },
        {
          term: "Satellite image",
          definition:
            "An image of Earth taken by a satellite."
        },
        {
          term: "Land use",
          definition:
            "How land is used, such as housing, industry, farming, retail, transport or recreation."
        },
        {
          term: "Human feature",
          definition:
            "A feature made or changed by people, such as roads, buildings, ports or farms."
        },
        {
          term: "Physical feature",
          definition:
            "A natural feature, such as rivers, hills, forests, beaches or mountains."
        },
        {
          term: "Annotation",
          definition:
            "A label or note added to an image, map or diagram to explain important features."
        }
      ],
      tutorial: [
        {
          heading: "Using plans",
          body:
            "Plans show the layout of a place from above. They can show streets, buildings, rooms, parks, services or land-use zones. Plans are useful for identifying patterns and relationships between features."
        },
        {
          heading: "Using photographs",
          body:
            "Photographs provide visual evidence. Ground photographs show detail and human experience. Aerial photographs and satellite images show wider patterns, such as settlement shape, land use and transport routes."
        },
        {
          heading: "Human and physical features",
          body:
            "When interpreting images, separate human features from physical features. A road, factory, housing estate or bridge is human. A river, beach, hill or woodland is physical."
        },
        {
          heading: "Limitations of photographs",
          body:
            "Photographs show only one moment and one viewpoint. They may not show what is outside the frame, when the image was taken, or whether the place changes at other times."
        }
      ],
      workedExamples: [
        {
          question:
            "What human geography features might you identify on an aerial photograph of a town?",
          answer:
            "You might identify housing estates, roads, railways, shops, factories, schools, parks, car parks, bridges and industrial estates."
        },
        {
          question:
            "Why might an aerial photograph be more useful than a ground photograph for studying land use?",
          answer:
            "An aerial photograph shows a wider area from above, so it is easier to see patterns of land use, transport routes and how different zones are connected."
        }
      ],
      commonMisconceptions: [
        "Photographs are evidence, but they still need interpretation.",
        "A photograph can show human and physical geography at the same time.",
        "Aerial photographs show patterns better, while ground photographs often show detail better."
      ],
      quickCheck: [
        {
          question:
            "Which is a human feature?",
          options: ["Road", "River", "Mountain", "Cliff"],
          answer: "Road",
          explanation:
            "A road is built by people, so it is a human feature."
        },
        {
          question:
            "Which photograph type is taken from above?",
          options: [
            "Aerial photograph",
            "Ground photograph",
            "Portrait photograph only",
            "Microscope image"
          ],
          answer: "Aerial photograph",
          explanation:
            "Aerial photographs are taken from above the ground."
        }
      ],
      examPractice: [
        {
          question:
            "Explain how photographs can be used in a geographical enquiry.",
          marks: 6,
          indicativeContent:
            "Photographs can provide visual evidence of human and physical features. Ground photographs show detail such as building condition, traffic, litter or land use. Aerial photographs show wider patterns such as settlement layout, road networks and green space. Photographs can be annotated and compared with maps or survey data, but they should be evaluated because they show only one viewpoint and one moment in time."
        }
      ],
      grade9Move:
        "When using photos, describe what you can see, infer what it suggests, then evaluate what the photo cannot show."
    },

    {
      id: "geo11-6-describing-maps-and-charts",
      title: "Describing Maps and Charts",
      slug: "describing-maps-and-charts",
      pageRef: "Source booklet p.156",
      learningObjectives: [
        "Describe patterns on maps and charts.",
        "Use accurate data in descriptions.",
        "Identify anomalies and trends."
      ],
      keywords: [
        {
          term: "Pattern",
          definition:
            "The arrangement or distribution of features or data."
        },
        {
          term: "Trend",
          definition:
            "A general direction of change, such as increase, decrease or fluctuation."
        },
        {
          term: "Anomaly",
          definition:
            "A value or feature that does not fit the general pattern."
        },
        {
          term: "Correlation",
          definition:
            "A relationship between two variables."
        },
        {
          term: "Positive correlation",
          definition:
            "A relationship where one variable increases as the other also increases."
        },
        {
          term: "Negative correlation",
          definition:
            "A relationship where one variable increases as the other decreases."
        },
        {
          term: "Distribution",
          definition:
            "How something is spread across an area."
        },
        {
          term: "Evidence",
          definition:
            "Data or information used to support a point."
        }
      ],
      tutorial: [
        {
          heading: "Describe before explaining",
          body:
            "When using maps and charts, first describe what the data shows. Then explain why the pattern might exist. Description uses evidence; explanation gives reasons."
        },
        {
          heading: "Use precise language",
          body:
            "Useful words include increase, decrease, fluctuate, peak, lowest, highest, cluster, spread, concentrated, sparse, dense, north, south, coastal, inland, near and far."
        },
        {
          heading: "Use data",
          body:
            "Strong descriptions use numbers from the map or chart. For example, instead of saying ‘it increases a lot’, write ‘it rises from 20 to 65 between 2000 and 2020’."
        },
        {
          heading: "Look for anomalies",
          body:
            "Anomalies are values or places that do not fit the pattern. Identifying anomalies makes your answer more accurate and analytical."
        }
      ],
      workedExamples: [
        {
          question:
            "Improve this description: ‘The graph goes up.’",
          answer:
            "A stronger description would be: ‘The value increases steadily from 25 in 2010 to 70 in 2020, with the fastest rise between 2016 and 2018.’"
        },
        {
          question:
            "How would you describe a map where most large cities are near the coast?",
          answer:
            "Most large cities are concentrated in coastal areas, especially along the eastern and southern coastlines. Fewer large cities are found inland, suggesting an uneven distribution."
        }
      ],
      commonMisconceptions: [
        "Description and explanation are different. Description says what; explanation says why.",
        "Do not ignore anomalies because they can be important.",
        "Use compass directions and data rather than vague words like ‘over there’ or ‘a lot’."
      ],
      quickCheck: [
        {
          question:
            "Which word describes a value that does not fit the pattern?",
          options: ["Anomaly", "Scale", "Easting", "Symbol"],
          answer: "Anomaly",
          explanation:
            "An anomaly is unusual compared with the general trend or pattern."
        },
        {
          question:
            "Which is the best data description?",
          options: [
            "It rises from 10 to 45 between 2000 and 2020",
            "It goes up loads",
            "It is nice",
            "It is somewhere on the chart"
          ],
          answer: "It rises from 10 to 45 between 2000 and 2020",
          explanation:
            "This uses precise data and a clear time period."
        }
      ],
      examPractice: [
        {
          question:
            "Describe how to write a strong description of a graph.",
          marks: 4,
          indicativeContent:
            "A strong graph description identifies the overall trend, uses accurate data, mentions the highest and lowest values where relevant, and identifies any anomalies. It should use precise language such as increase, decrease, fluctuate, peak and decline."
        },
        {
          question:
            "Explain the difference between describing and explaining a map pattern.",
          marks: 4,
          indicativeContent:
            "Describing a map pattern means saying what the map shows, such as where features are clustered or sparse. Explaining means giving reasons for the pattern, such as climate, relief, transport, jobs or resources. Description should come before explanation."
        }
      ],
      grade9Move:
        "Use the structure: overall pattern → specific evidence → anomaly → possible explanation."
    },

    {
      id: "geo11-7-types-of-graphs-and-charts",
      title: "Types of Graphs and Charts",
      slug: "types-of-graphs-and-charts",
      pageRef: "Source booklet p.158",
      learningObjectives: [
        "Identify common types of graphs and charts.",
        "Choose suitable graphs for different data.",
        "Interpret and evaluate graphs accurately."
      ],
      keywords: [
        {
          term: "Bar chart",
          definition:
            "A chart using separate bars to compare categories."
        },
        {
          term: "Line graph",
          definition:
            "A graph showing change over time or continuous data using a line."
        },
        {
          term: "Pie chart",
          definition:
            "A circular chart divided into sectors to show proportions or percentages."
        },
        {
          term: "Scatter graph",
          definition:
            "A graph used to show the relationship between two variables."
        },
        {
          term: "Climate graph",
          definition:
            "A graph showing average monthly temperature and rainfall for a place."
        },
        {
          term: "Histogram",
          definition:
            "A graph showing grouped continuous data using bars that touch."
        },
        {
          term: "Choropleth map",
          definition:
            "A map using shading or colour to show data values for areas."
        },
        {
          term: "Proportional symbol map",
          definition:
            "A map using symbols of different sizes to show data values."
        },
        {
          term: "Desire line map",
          definition:
            "A map showing movement flows between places using lines."
        }
      ],
      tutorial: [
        {
          heading: "Choosing the right graph",
          body:
            "Different data needs different presentation. Bar charts compare categories. Line graphs show change over time. Pie charts show proportions. Scatter graphs show relationships between two variables. Climate graphs show rainfall and temperature across months."
        },
        {
          heading: "Bar charts and line graphs",
          body:
            "Use a bar chart for separate categories, such as land use types or transport modes. Use a line graph for continuous change, especially over time, such as population growth or river discharge after rainfall."
        },
        {
          heading: "Pie charts",
          body:
            "Pie charts are useful when showing parts of a whole, such as percentage of employment in each economic sector. They are less useful if there are too many categories."
        },
        {
          heading: "Scatter graphs",
          body:
            "Scatter graphs show whether two variables are related. If points rise from left to right, there is positive correlation. If points fall from left to right, there is negative correlation. If there is no clear pattern, correlation is weak or absent."
        },
        {
          heading: "Maps with data",
          body:
            "Choropleth maps show area-based data using shading. Proportional symbol maps show values using different-sized symbols. Flow or desire line maps show movement between places."
        }
      ],
      workedExamples: [
        {
          question:
            "Which graph would you use to show population change from 1950 to 2020?",
          answer:
            "A line graph, because it shows change over time."
        },
        {
          question:
            "Which chart would you use to show the percentage of workers in primary, secondary, tertiary and quaternary sectors?",
          answer:
            "A pie chart could be used because the data shows parts of a whole. A divided bar chart could also work."
        },
        {
          question:
            "Which graph would show the relationship between distance from the CBD and land value?",
          answer:
            "A scatter graph would be suitable because it shows the relationship between two variables."
        }
      ],
      commonMisconceptions: [
        "Do not use a line graph for unrelated categories.",
        "Pie charts should add up to 100% or represent a whole.",
        "A scatter graph can show correlation, but correlation does not always prove causation."
      ],
      quickCheck: [
        {
          question:
            "Which graph is best for showing change over time?",
          options: ["Line graph", "Pie chart", "Sketch map", "Key"],
          answer: "Line graph",
          explanation:
            "Line graphs are best for continuous data or change over time."
        },
        {
          question:
            "Which graph is best for comparing separate categories?",
          options: ["Bar chart", "Line graph", "Contour map", "Compass rose"],
          answer: "Bar chart",
          explanation:
            "Bar charts compare categories using separate bars."
        },
        {
          question:
            "Which graph shows correlation between two variables?",
          options: ["Scatter graph", "Pie chart", "Pictogram only", "OS key"],
          answer: "Scatter graph",
          explanation:
            "Scatter graphs are used to identify relationships between variables."
        }
      ],
      examPractice: [
        {
          question:
            "Choose a suitable graph to show monthly rainfall and temperature, and explain your choice.",
          marks: 4,
          indicativeContent:
            "A climate graph would be suitable because it shows both rainfall and temperature for each month. Rainfall is usually shown as bars and temperature as a line, allowing seasonal patterns to be compared."
        },
        {
          question:
            "Explain why choosing the correct graph or chart is important in geography.",
          marks: 6,
          indicativeContent:
            "Choosing the correct graph or chart is important because it helps data communicate the pattern clearly. A line graph is suitable for change over time, while a bar chart is better for categories. A pie chart shows proportions, and a scatter graph shows relationships between variables. If the wrong graph is chosen, the data may be confusing or misleading."
        },
        {
          question:
            "Evaluate the usefulness of a scatter graph in a geographical enquiry.",
          marks: 6,
          indicativeContent:
            "A scatter graph is useful because it can show whether two variables are related, such as distance from a river source and river width. It can show positive, negative or weak correlation and can reveal anomalies. However, it does not prove that one variable causes the other, and results may be affected by sample size or measurement error."
        }
      ],
      grade9Move:
        "When choosing a graph, justify the choice using the data type: categories, time series, proportions, spatial pattern or correlation."
    }
  ],

  sectionReview: {
    title: "Section Eleven Review — Geographical Enquiry and Skills",
    summary:
      "Geographical enquiry is a structured way of investigating places, patterns and processes. Geographers ask focused questions, collect primary and secondary data, present information using maps, graphs and charts, analyse evidence, reach conclusions and evaluate methods. Key skills include OS map reading, grid references, scale, contour interpretation, photo analysis, source evaluation and choosing appropriate graphs.",
    mustKnow: [
      "Geographical enquiry is a structured investigation into a geographical question.",
      "A good enquiry question is focused, geographical and possible to investigate.",
      "Primary data is collected first-hand by the investigator.",
      "Secondary data has already been collected by someone else.",
      "Quantitative data is numerical; qualitative data is descriptive.",
      "Reliable sources are accurate, relevant, up to date and as unbiased as possible.",
      "Fieldwork methods should be planned carefully and evaluated.",
      "Sampling means selecting part of a group or area to represent the whole.",
      "Grid references use eastings first, then northings.",
      "Four-figure grid references identify a grid square; six-figure references give a more precise location.",
      "Contour lines show height and relief.",
      "Close contour lines mean steep slopes; wider-spaced contours mean gentler slopes.",
      "Plans, photos, aerial images and satellite images can be used as geographical evidence.",
      "Descriptions of maps and charts should use patterns, data and anomalies.",
      "Bar charts compare categories.",
      "Line graphs show change over time.",
      "Pie charts show proportions of a whole.",
      "Scatter graphs show relationships between two variables.",
      "Climate graphs show temperature and rainfall across the year.",
      "Evaluation considers reliability, limitations and improvements."
    ],
    highValueExamPhrases: [
      "The enquiry question is focused because it can be investigated using measurable data...",
      "Primary data was collected first-hand, while secondary data was used for comparison...",
      "The method could be improved by increasing the sample size...",
      "This source may be unreliable because it is out of date or biased...",
      "The overall pattern shows...",
      "An anomaly is visible where...",
      "The data supports the conclusion because...",
      "Eastings are read before northings...",
      "Close contour lines indicate steep relief...",
      "A scatter graph is appropriate because it shows correlation between two variables...",
      "The graph choice is suitable because the data shows change over time..."
    ],
    mixedPractice: [
      {
        question:
          "Explain the stages of a geographical enquiry.",
        marks: 6,
        indicativeContent:
          "A geographical enquiry starts with a focused question or hypothesis. The investigator plans the method, including location, equipment, sampling and safety. Data is collected through fieldwork or secondary sources. The data is then presented using maps, graphs or tables. Patterns are analysed, a conclusion is reached using evidence, and the method is evaluated for reliability and improvements."
      },
      {
        question:
          "Explain the difference between primary and secondary data.",
        marks: 4,
        indicativeContent:
          "Primary data is collected first-hand by the investigator, such as traffic counts, questionnaires or river measurements. Secondary data has already been collected by someone else, such as census data, OS maps, websites or climate statistics. Both can be useful, but both need to be checked for reliability."
        },
      {
        question:
          "Describe how to use a six-figure grid reference.",
        marks: 4,
        indicativeContent:
          "Read the easting first to identify the grid square, then estimate tenths across the square. Read the northing next and estimate tenths up the square. Write the three easting digits followed by the three northing digits to give a precise location."
        },
      {
        question:
          "Explain how contour lines help geographers understand relief.",
        marks: 4,
        indicativeContent:
          "Contour lines join places of equal height above sea level. If the lines are close together, the slope is steep because height changes quickly. If the lines are far apart, the land is gently sloping. The height numbers show whether land is higher or lower."
        },
      {
        question:
          "Choose a suitable graph for showing change in population over time and explain your choice.",
        marks: 4,
        indicativeContent:
          "A line graph is suitable because population over time is continuous data. The line makes it easy to identify increases, decreases, trends, fluctuations and anomalies."
        },
      {
        question:
          "Evaluate how reliable a fieldwork enquiry might be.",
        marks: 8,
        indicativeContent:
          "Reliability depends on the method, sample size, timing, accuracy of measurements and whether the data is representative. A larger sample and repeated measurements improve reliability. Systematic sampling can reduce bias. However, results may be affected by weather, time of day, observer error or unclear categories. The enquiry could be improved by repeating it at different times, using more sites and comparing primary data with secondary sources."
      }
    ]
  }
};

export default geographyContent11;